import React, { useState, useEffect } from 'react';
import { Phone, ArrowLeft, Plus, Copy, Trash2, GripVertical, Check, Users, Play, Star } from 'lucide-react';
import { featureItems } from '../data/mock';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_ruonn-replica/artifacts/652j8z6m_logo%20letra%20transparente%20white.png';

const HeroSection = ({ onOpenModal }) => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [completedItems, setCompletedItems] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % featureItems.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const toggleComplete = (id) => {
    setCompletedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  return (
    <section className="min-h-screen bg-[#0a0a0b] pt-32 pb-20 px-6 flex flex-col items-center justify-center relative overflow-hidden">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/5 via-[#0a0a0b] to-[#0a0a0b] pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      
      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto text-center">
        {/* Urgency Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 text-sm mb-8 animate-pulse">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
          <span className="text-gray-200">🔥 <strong className="text-white">47 companies</strong> hired this week</span>
        </div>

        {/* Main Headline */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-normal text-white leading-tight mb-6">
          Stop wasting months<br />
          <span className="italic font-light">hiring developers.</span><br />
          <span className="font-medium bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Get matched in 72 hours.</span>
        </h1>

        {/* Subtitle with value prop */}
        <p className="text-gray-400 text-xl mb-4 max-w-2xl mx-auto">
          Access pre-vetted senior engineers and QA specialists.
          <span className="text-white font-medium"> Satisfaction guaranteed. </span>
          Full replacement if not 100% happy.
        </p>

        {/* Social proof mini */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <span className="text-gray-400">Rated 4.9/5 by 500+ companies</span>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
          <button
            onClick={onOpenModal}
            className="group inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white px-8 py-4 rounded-full text-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/30"
          >
            <Phone className="w-5 h-5" />
            Get Your Free Consultation
            <span className="text-purple-200 group-hover:translate-x-1 transition-transform">→</span>
          </button>
          <a
            href="#how-it-works"
            className="inline-flex items-center gap-2 text-gray-300 hover:text-white px-6 py-4 rounded-full text-base font-medium transition-all duration-300"
          >
            <Play className="w-5 h-5" />
            See how it works
          </a>
        </div>

        {/* Trust line */}
        <p className="text-gray-500 text-sm">
          ✓ No credit card required  ✓ Cancel anytime  ✓ 100% satisfaction guarantee
        </p>
      </div>

      {/* Feature Card */}
      <div className="relative z-10 mt-16 w-full max-w-lg">
        <div className="bg-[#18181b] rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
          {/* Card Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
            <div className="flex items-center gap-3">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
              <span className="text-white font-medium">With Ruonn</span>
            </div>
            <Plus className="w-5 h-5 text-gray-400" />
          </div>

          {/* Card Content */}
          <div className="p-4 space-y-1">
            {featureItems.map((item, index) => (
              <div
                key={item.id}
                className={`flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-500 ${
                  index === currentFeature
                    ? 'bg-gradient-to-r from-purple-900/40 via-purple-800/20 to-transparent'
                    : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleComplete(item.id)}
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      completedItems.includes(item.id)
                        ? 'bg-[#7c3aed] border-[#7c3aed]'
                        : 'border-gray-500 hover:border-gray-400'
                    }`}
                  >
                    {completedItems.includes(item.id) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </button>
                  <span className={`text-gray-300 transition-all duration-300 ${
                    completedItems.includes(item.id) ? 'line-through opacity-50' : ''
                  }`}>
                    {item.text}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-500">
                  <Copy className="w-4 h-4 hover:text-gray-300 cursor-pointer transition-colors" />
                  <Trash2 className="w-4 h-4 hover:text-gray-300 cursor-pointer transition-colors" />
                  <GripVertical className="w-4 h-4 hover:text-gray-300 cursor-pointer transition-colors" />
                </div>
              </div>
            ))}
          </div>

          {/* Progress Indicator */}
          <div className="px-5 pb-4">
            <div className="text-xs text-gray-500 mb-2">Completed {currentFeature + 1}/{featureItems.length}</div>
            <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full transition-all duration-500"
                style={{ width: `${((currentFeature + 1) / featureItems.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Gradient glow effect */}
        <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-96 h-40 bg-purple-600/20 blur-3xl rounded-full pointer-events-none" />
      </div>
    </section>
  );
};

export default HeroSection;
